// «COBRAR SIN SEÑAL», EL AJUSTE DE LA EMPRESA · DIRECTRIZ-PWA F2 (11 oct
// 2026, DISENO-F2 §9, R-PWA-6, D-11).
//
// Un interruptor y su consecuencia, dicha donde se decide. Lee y escribe
// `/ventas/configuracion` (solo `cobrar_sin_senal`: el PUT escribe lo que
// viene y nada más). Encender pide confirmación con la consecuencia;
// apagar no, porque es la opción segura. El módulo es `min: 'ADMIN'` y el
// servidor exige administrador igual.
import { useCallback, useEffect, useState } from 'react';
import { WifiOff, Loader2 } from 'lucide-react';
import { api } from '../../api';
import Ayuda from '../../ui/Ayuda';
import ModalConfirmar from '../../ui/ModalConfirmar';
import { fechaExacta } from '../../ui/tiempo';

const RUTA = '/ventas/configuracion';
const BOTON_SECUNDARIO = 'min-h-11 px-4 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 '
  + 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]';

export default function CobrarSinSenal() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [tardando, setTardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [intento, setIntento] = useState(0);

  const leer = useCallback(() => api.get(RUTA), []);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => { if (vivo) setTardando(true); }, 300);
    leer()
      .then((c) => { if (vivo) { setConfig(c); setError(null); } })
      .catch((e) => { if (vivo) setError(e.message); })
      .finally(() => clearTimeout(t));
    return () => { vivo = false; clearTimeout(t); };
  }, [leer, intento]);

  async function escribir(valor) {
    setGuardando(true);
    setError(null);
    try {
      const c = await api.put(RUTA, { cobrar_sin_senal: valor });
      setConfig(c);
      setAviso(valor
        ? 'Encendido. Desde ahora el punto de venta cobra en efectivo sin señal.'
        : 'Apagado. Las ventas ya guardadas sin señal se enviarán igual.');
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setGuardando(false);
    }
  }

  const encendido = config?.cobrar_sin_senal === true;

  return (
    <div className="mx-auto w-full max-w-prose space-y-4">
      <div className="flex items-start gap-3">
        <WifiOff className="h-7 w-7 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">Cobrar sin señal</h1>
          <p className="text-base text-gray-600">Si la caja puede cobrar en efectivo cuando el teléfono no tiene señal. Viene apagado.</p>
        </div>
      </div>
      <Ayuda id="pwa.ajuste.pantalla" />

      {config === null && !error && tardando && (
        <p role="status" className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo el ajuste…
        </p>
      )}

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-2">
          <p className="break-words">{error}</p>
          <button type="button" onClick={() => setIntento((n) => n + 1)} className={BOTON_SECUNDARIO}>Reintentar</button>
        </div>
      )}

      {config !== null && (
        <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span id="ajuste-cobrar-sin-senal-nombre" className="text-base font-semibold text-gray-900 min-w-0">
              Permitir cobrar en efectivo sin señal
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={encendido}
              aria-labelledby="ajuste-cobrar-sin-senal-nombre"
              aria-busy={guardando}
              disabled={guardando}
              onClick={() => (encendido ? escribir(false).catch(() => {}) : setConfirmando(true))}
              className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] ${
                encendido ? 'border-green-300 bg-green-50 text-green-900' : 'border-gray-300 bg-gray-50 text-gray-700'}`}
            >
              <span aria-hidden="true" className={`inline-block h-3 w-3 rounded-full ${encendido ? 'bg-green-600' : 'bg-gray-400'}`} />
              {encendido ? 'Encendido' : 'Apagado'}
            </button>
          </div>
          <Ayuda id="pwa.ajuste.cobrar_sin_senal" />
          <p role="status" className="text-sm text-gray-700">
            {encendido
              ? `Encendido.${config.actualizado_en ? ` Último cambio de la configuración de ventas: ${fechaExacta(config.actualizado_en)}` : ''}${config.actualizado_por ? `, por ${config.actualizado_por}` : ''}${config.actualizado_en || config.actualizado_por ? '.' : ''}`
              : 'Apagado: sin señal, el punto de venta no cobra.'}
          </p>
          {aviso && <p role="status" className="text-sm font-semibold text-green-800">{aviso}</p>}
        </section>
      )}

      <p className="text-sm text-gray-600">
        Aunque esté apagado, una venta que ya se guardó sin señal se envía y queda marcada como cobrada sin señal.
      </p>

      {confirmando && (
        <ModalConfirmar
          titulo="¿Permitir cobrar en efectivo sin señal?"
          descripcion="Las ventas sin señal se guardan en el teléfono y se envían al volver la señal, sin comprobar la existencia en ese momento. Si para entonces no hay producto, la venta queda por revisar. Tarjeta nunca se cobra sin señal."
          etiquetaConfirmar="Sí, permitirlo"
          onConfirmar={() => escribir(true)}
          onCerrar={() => setConfirmando(false)}
        />
      )}
    </div>
  );
}
